'use client';

import { useState, useCallback, useMemo } from 'react';
import { Calculator, TrendingUp, Wallet, DollarSign } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import type { AIFeaturesContent } from '@/core/i18n/content/ai-features.types';

interface AIFeaturesROIProps {
  content: AIFeaturesContent['roi'];
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

export function AIFeaturesROI({ content }: AIFeaturesROIProps) {
  const [rooms, setRooms] = useState(80);
  const [adr, setAdr] = useState(120);
  const [occupancy, setOccupancy] = useState(65);

  const handleRoomsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRooms(Number(e.target.value));
  }, []);

  const handleAdrChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAdr(Number(e.target.value));
  }, []);

  const handleOccupancyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setOccupancy(Number(e.target.value));
  }, []);

  const results = useMemo(() => {
    const annualRevenue = rooms * adr * (occupancy / 100) * 365;
    const additionalRevenue = Math.round(annualRevenue * 0.18);
    const operationalSavings = Math.round(annualRevenue * 0.08);
    const totalImpact = additionalRevenue + operationalSavings;
    return { additionalRevenue, operationalSavings, totalImpact };
  }, [rooms, adr, occupancy]);

  return (
    <section id="ai-features-roi" className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-24">
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 50%, rgba(200,145,58,0.05) 0%, transparent 70%)' }} />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <Calculator className="mx-auto mb-4 h-8 w-8 text-hms-gold" />
          <h2 id="ai-features-roi-title" className={cn('text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}>
            {content.title}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-hms-textSub sm:text-base">{content.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Inputs */}
          <div id="ai-features-roi-inputs" className="space-y-6 rounded-xl border border-hms-border bg-hms-card p-6">
            <SliderInput id="ai-features-roi-rooms" label={content.roomsLabel} value={rooms} min={10} max={500} step={5} onChange={handleRoomsChange} suffix="" />
            <SliderInput id="ai-features-roi-adr" label={content.adrLabel} value={adr} min={30} max={500} step={5} onChange={handleAdrChange} suffix="$" />
            <SliderInput id="ai-features-roi-occupancy" label={content.occupancyLabel} value={occupancy} min={20} max={100} step={1} onChange={handleOccupancyChange} suffix="%" />
          </div>

          {/* Results */}
          <div id="ai-features-roi-results" className="rounded-xl border border-hms-gold/20 bg-gradient-to-br from-hms-card to-hms-surface p-6">
            <h3 className="mb-6 text-lg font-bold text-hms-text">{content.resultTitle}</h3>
            <div className="space-y-5">
              <ResultRow icon={TrendingUp} label={content.revenueLabel} value={formatCurrency(results.additionalRevenue)} suffix={content.perYearSuffix} color="text-hms-green" />
              <ResultRow icon={Wallet} label={content.savingsLabel} value={formatCurrency(results.operationalSavings)} suffix={content.perYearSuffix} color="text-hms-teal" />
              <div className="border-t border-hms-border pt-4">
                <ResultRow icon={DollarSign} label={content.totalLabel} value={formatCurrency(results.totalImpact)} suffix={content.perYearSuffix} color="text-hms-gold" large />
              </div>
            </div>
            <p className="mt-6 text-[11px] text-hms-textDim">{content.disclaimer}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Slider Input                                                               */
/* -------------------------------------------------------------------------- */

function SliderInput({ id, label, value, min, max, step, onChange, suffix }: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  suffix: string;
}) {
  const displayValue = suffix === '$' ? `$${value}` : suffix === '%' ? `${value}%` : `${value}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label htmlFor={id} className="text-sm font-medium text-hms-textSub">{label}</label>
        <span className="text-sm font-bold text-hms-gold">{displayValue}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        className={cn(
          'w-full h-1.5 rounded-full appearance-none cursor-pointer',
          'bg-hms-border',
          '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4',
          '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-hms-gold',
          '[&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:shadow-hms-gold/30',
          '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4',
          '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-hms-gold [&::-moz-range-thumb]:border-0',
        )}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Result Row                                                                 */
/* -------------------------------------------------------------------------- */

function ResultRow({ icon: Icon, label, value, suffix, color, large }: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  suffix: string;
  color: string;
  large?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', large ? 'bg-hms-gold/15' : 'bg-hms-gold/10')}>
          <Icon className={cn('h-4.5 w-4.5', color)} />
        </div>
        <span className={cn('font-medium', large ? 'text-base text-hms-text' : 'text-sm text-hms-textSub')}>{label}</span>
      </div>
      <div className="text-right">
        <span className={cn('font-bold tabular-nums', color, large ? 'text-xl' : 'text-lg')}>{value}</span>
        <span className="ml-1 text-[11px] text-hms-textDim">{suffix}</span>
      </div>
    </div>
  );
}
